import { useCallback, useEffect, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

export type OpexViewMode = "unlinked" | "linked" | "archived" | "all";

export interface OpexInvoiceCounts {
  unlinked: number;
  linked: number;
  archived: number;
  all: number;
}

interface Params {
  viewMode: OpexViewMode;
  buildingId?: string;
  search?: string;
  page: number;
  pageSize: number;
}

/**
 * Paginagewijs ophalen van OPEX-factuurregels, met knoppen in plaats van
 * oneindig scrollen.
 *
 * Odoo pagineert al server-side; deze hook houdt enkel de huidige pagina bij en
 * haalt die op. De tellers per weergave komen uit een aparte lichte call (drie
 * `search_count`-queries) zodat de tabs kloppen zonder regels op te halen.
 */
export const useOpexInvoices = <T = unknown>({
  viewMode,
  buildingId,
  search,
  page,
  pageSize,
}: Params) => {
  const { session } = useSession();
  const [invoices, setInvoices] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState<OpexInvoiceCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Bumpen forceert een herophaling na koppelen/archiveren. */
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      sortField: "invoiceDate",
      sortDirection: "DESC",
      viewMode,
    });
    if (buildingId) params.set("buildingId", buildingId);
    if (search) params.set("search", search);

    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/opex-invoices?${params.toString()}`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(`Ophalen mislukt (${response.status})`);
        }

        const result = await response.json();
        if (cancelled) return;

        setInvoices(result.data ?? []);
        setTotalCount(result.pagination?.total ?? 0);
      } catch (fetchError) {
        if (cancelled) return;
        console.error("Error fetching OPEX invoices:", fetchError);
        setError(
          fetchError instanceof Error ? fetchError.message : "Ophalen mislukt",
        );
        setInvoices([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, viewMode, buildingId, search, page, pageSize, refreshToken]);

  // Tellers volgen dezelfde filters, maar niet de weergave of de pagina.
  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    const params = new URLSearchParams();
    if (buildingId) params.set("buildingId", buildingId);
    if (search) params.set("search", search);

    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/opex-invoices/counts?${params.toString()}`,
          { method: "GET" },
          session,
        );
        if (!response.ok || cancelled) return;
        setCounts(await response.json());
      } catch (countError) {
        // Tellers zijn informatief: falen mag de lijst niet breken.
        console.error("Error fetching OPEX counts:", countError);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, buildingId, search, refreshToken]);

  return {
    invoices,
    setInvoices,
    totalCount,
    counts,
    isLoading,
    error,
    refresh,
  };
};
